import { Col, Container, Row, Badge, Tabs, Tab, Card } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { useParamIds } from "./Data/Common";
import { showDuration, showDateTime, showTime } from "./Data/Date";
import { showMoneyShort, toCents } from "./Data/Money";
import { RateProgram, ValidationType, RateEntryDiscountType } from "./Data/ApiTypes";
import { showApplicableDays } from "./Data/RateProgram";
import { PencilFill, QrCode, InfoCircleFill, Calculator, Lightning, CircleFill, Plus, TicketPerforatedFill, QuestionCircle } from "react-bootstrap-icons";
import { LocalDateTime } from "@js-joda/core";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { Api } from "./Data/Api";
import { useState, useEffect, type ReactNode } from "react";
import { FacilityRateTesterModal } from "./FacilityRateTesterModal";
import { ValidationsInfoModal } from "./ValidationsInfoModal";

export function FacilityRateListPage() {
    const nav = useNavigate();
    const { facilityId } = useParamIds();
    const ratePrograms = useLoaderData() as RateProgram[];
    const [searchParams] = useSearchParams();
    const [programs, setPrograms] = useState(ratePrograms);
    const [showInactiveTraditionalRates, setShowInactiveTraditionalRates] = useState(false);
    const [showInactiveEventRates, setShowInactiveEventRates] = useState(false);
    const [showInactiveValidationRates, setShowInactiveValidationRates] = useState(false);
    const [showValidationsInfoModal, setShowValidationsInfoModal] = useState(false);
    const [showRateTesterModal, setShowRateTesterModal] = useState(false);
    
    // Initialize active tab from URL parameter or default to "hourly"
    const getInitialTab = (): "hourly" | "event" | "validation" => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'event' || tabParam === 'validation' || tabParam === 'hourly') {
            return tabParam;
        }
        return "hourly";
    };
    
    const [activeTab, setActiveTab] = useState<"hourly" | "event" | "validation">(getInitialTab());

    // Update active tab when URL parameter changes
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'event' || tabParam === 'validation' || tabParam === 'hourly') {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const handleToggleActive = async (rateProgramId: number, isActive: boolean) => {
        await Api.facilityRateStatusUpdate(facilityId, rateProgramId, isActive).then(res => {
            setPrograms(prev =>
                prev.map(p =>
                    p.rateProgramId === rateProgramId ? { ...p, isActive } : p
                )
            );
        }).catch(err => {
            console.error("Failed to change rate status:", err);
        });
    }

    const editRateButton = (rateProgramId: number) => (
        <Button
            variant="sm"
            className="d-flex align-items-center"
            style={{ padding: "1px 0.5em" }}
            onClick={() => nav(`/facility/${facilityId}/rate/${rateProgramId}`)}
        >
            <PencilFill color="maroon" /> &nbsp; Edit Rate
        </Button>
    );

    const rateActiveControls = (prog: RateProgram) => (
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <div className="form-check form-switch mb-0">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id={`toggle-${prog.rateProgramId}`}
                    checked={prog.isActive}
                    onChange={(e) => handleToggleActive(prog.rateProgramId, e.target.checked)}
                />
            </div>
            <Badge bg={prog.isActive ? "success" : "secondary"}>
                {prog.isActive ? "Active" : "Inactive"}
            </Badge>
        </div>
    );

    return <Container fluid className="px-0 px-sm-3" style={{ overflowY: "hidden"}}>
        
        <Row className="justify-content-left mb-3">
            <Col xs="auto">
                <Button className="w-100 btn-main mt-1" onClick={() => nav( `/facility/${facilityId}/rate/create?tab=${activeTab}` )}>
                    Create New Rate
                </Button>
            </Col>
        </Row>
        <Row className="justify-content-left">
            <Col xl="6" xxl="5" className="mb-2">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as typeof activeTab)}>
            <Tab eventKey="hourly" title="Hourly">
                {programs?.filter(r => !r.isEvent && !r.isValidation).length > 0 && <>
                    {(() => {
                        const now = LocalDateTime.now();
                        const activeOverrides = programs?.filter(p =>
                            p.isSpecialRate && p.isActive
                            && now.compareTo(p.start) >= 0
                            && (p.end == null || now.compareTo(p.end) <= 0)
                        ) ?? [];
                        return activeOverrides.length > 0 && <div className="mt-3">
                            {activeOverrides.map(prog => (
                                <div key={prog.rateProgramId} className="alert alert-muted-gold d-flex align-items-center gap-2 mb-2"
                                    style={{ fontSize: "0.9rem" }}>
                                    <Lightning className="flex-shrink-0" />
                                    <div className="flex-fill">
                                        <strong>{prog.name}</strong> is overriding hourly rates
                                        <div className="text-muted">
                                            {showDateTime(prog.start)} - {prog.end ? showDateTime(prog.end) : 'Open-ended'}
                                        </div>
                                    </div>
                                    <Badge bg="success" className="d-flex align-items-center gap-1" style={{ fontSize: ".75rem" }}>
                                        <CircleFill size={6} className="pulse-dot" /> Active Now
                                    </Badge>
                                </div>
                            ))}
                        </div>;
                    })()}
                    <div className="d-flex flex-wrap gap-2 my-3 align-items-center">
                        <ToolButton onClick={() => nav( `/facility/${facilityId}/rate/sign` )}>
                            <QrCode color="maroon" /> QR Code
                        </ToolButton>
                        <ToolButton onClick={() => setShowRateTesterModal(true)}>
                            <Calculator color="maroon" /> Calculator
                        </ToolButton>
                        <ToolButton onClick={() => nav( `/facility/${facilityId}/rate/create?tab=override` )}>
                            <Plus color="maroon" /> Special Rate
                        </ToolButton>
                    </div>
                        <div className="form-check form-switch mb-3 mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showInactiveToggle"
                                checked={showInactiveTraditionalRates}
                                onChange={() => setShowInactiveTraditionalRates(!showInactiveTraditionalRates)}
                            />
                            <label className="form-check-label" htmlFor="showInactiveToggle">
                                Show Inactive Rates
                            </label>
                        </div>
                        {programs?.filter(r => !r.isEvent && !r.isValidation && !r.isSpecialRate)
                        .filter(r => showInactiveTraditionalRates || r.isActive)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(prog =>
                        <Card key={prog.rateProgramId} className="mb-3">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        {prog.start && new Date(prog.start.toString()) > new Date() && (
                                            <Badge>Future</Badge>
                                        )}
                                        {(prog.end && new Date(prog.end.toString()) < new Date()) && (
                                            <Badge bg="secondary">Ended</Badge>
                                        )}
                                        <span className="fs-5 fw-bold">{prog.name}</span>
                                    </div>
                                    {rateActiveControls(prog)}
                                </div>
                                <RateProgramTable prog={prog} headerAction={editRateButton(prog.rateProgramId)} />
                            </Card.Body>
                        </Card> )}

                        {programs?.filter(r => r.isSpecialRate)
                        .filter(r => showInactiveTraditionalRates || r.isActive)
                        .length > 0 && <>
                            <div className="mt-4 mb-2">
                                <span className="fs-6 fw-bold text-muted text-uppercase" style={{ letterSpacing: "0.05em" }}>
                                    Special Rate Overrides
                                </span>
                            </div>
                            {programs.filter(r => r.isSpecialRate)
                            .filter(r => showInactiveTraditionalRates || r.isActive)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(prog => {
                                const now = LocalDateTime.now();
                                const isInWindow = prog.isActive
                                    && now.compareTo(prog.start) >= 0
                                    && (prog.end == null || now.compareTo(prog.end) <= 0);
                                const isFuture = now.compareTo(prog.start) < 0;
                                const isEnded = prog.end != null && now.compareTo(prog.end) > 0;

                                return <Card key={prog.rateProgramId} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                <Badge bg="warning" text="dark"
                                                    className="bg-opacity-75 d-flex align-items-center"
                                                    style={{ fontSize: ".75rem" }}>
                                                    Override
                                                </Badge>
                                                {isInWindow && (
                                                    <Badge bg="success" className="d-flex align-items-center gap-1"
                                                        style={{ fontSize: ".75rem" }}>
                                                        <CircleFill size={6} className="pulse-dot" /> Active Now
                                                    </Badge>
                                                )}
                                                {isFuture && <Badge>Upcoming</Badge>}
                                                {isEnded && <Badge bg="secondary">Ended</Badge>}
                                                <span className="fs-5 fw-bold">{prog.name}</span>
                                            </div>
                                            {rateActiveControls(prog)}
                                        </div>
                                        <div className="text-muted mb-2" style={{ fontSize: ".85rem" }}>
                                            {showDateTime(prog.start)}
                                            {prog.end ? <> &mdash; {showDateTime(prog.end)}</> : <> &mdash; Open-ended</>}
                                        </div>
                                        <RateProgramTable prog={prog} headerAction={editRateButton(prog.rateProgramId)} />
                                    </Card.Body>
                                </Card>; })}
                        </>}
                </>}
                </Tab>
                <Tab eventKey="event" title="Events">
                   
                    <div className="form-check form-switch mb-3 mt-3">
                        <input
                        className="form-check-input"
                        type="checkbox"
                        id="showInactiveToggleValidation"
                        checked={showInactiveEventRates}
                        onChange={() => setShowInactiveEventRates(!showInactiveEventRates)}
                        />
                        <label className="form-check-label" htmlFor="showInactiveToggleValidation">
                        Show Inactive Rates
                        </label>
                    </div>
                    <div className="alert mb-3 d-flex align-items-center gap-2 alert-light-bg">
                        <InfoCircleFill />
                        Events can only be accessed via a direct link.
                    </div>
                    {programs?.filter(r => r.isEvent).length > 0 && <>
                    {programs?.filter(r => r.isEvent)
                    .filter(r => showInactiveEventRates || r.isActive)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(prog =>
                    <Card key={prog.rateProgramId} className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                <span className="fs-5 fw-bold">{prog.name}</span>
                                {rateActiveControls(prog)}
                            </div>
                            <RateProgramTable
                                prog={prog}
                                headerAction={
                                    <div className="d-flex gap-2">
                                        {prog.isActive && (
                                            <Button
                                                variant="sm"
                                                className="d-flex align-items-center"
                                                style={{ padding: "1px 0.5em" }}
                                                onClick={() => nav(`/facility/${facilityId}/rate/sign/${prog.rateProgramId}`)}
                                            >
                                                <QrCode color="maroon" /> &nbsp; QR Code
                                            </Button>
                                        )}
                                        {editRateButton(prog.rateProgramId)}
                                    </div>
                                }
                            />
                        </Card.Body>
                    </Card> )}
                </>}
                </Tab>
                <Tab eventKey="validation" title="Validations">
              
                    <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                        <div
                            role="button"
                            tabIndex={0}
                            className="text-muted d-flex align-items-center gap-1"
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowValidationsInfoModal(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setShowValidationsInfoModal(true);
                                }
                            }}
                            title="About validations"
                        >
                            <QuestionCircle />
                            <span className="small">About validations</span>
                        </div>
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showInactiveToggleValidation"
                                checked={showInactiveValidationRates}
                                onChange={() => setShowInactiveValidationRates(!showInactiveValidationRates)}
                            />
                            <label className="form-check-label" htmlFor="showInactiveToggleValidation">
                                Show Inactive Rates
                            </label>
                        </div>
                    </div>

                    {programs?.filter(r => r.isValidation).length > 0 && 
                     <>
                    {programs
                    .filter(r => r.isValidation)
                    .filter(r => showInactiveValidationRates || r.isActive)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(prog => {
                        const isDiscount = prog.validationType === ValidationType.Discount;
                        const isCode = prog.validationType === ValidationType.Code;
                        const badgeLabel = isCode ? "Code" : isDiscount ? "Discount" : "Fixed";
                        const selectedId = isDiscount ? prog.encodedRateProgramId : prog.rateProgramId;
                        const qrUrl = `/facility/${facilityId}/rate/sign/${selectedId}/true/${isDiscount}`;

                        const validationActions = (
                            <div className="d-flex gap-2">
                                {prog.isActive && !isCode && (
                                    <Button
                                        variant="sm"
                                        className="d-flex align-items-center"
                                        style={{ padding: "1px 0.5em" }}
                                        onClick={() => nav(qrUrl)}
                                    >
                                        <QrCode color="maroon" /> &nbsp; QR Code
                                    </Button>
                                )}
                                {editRateButton(prog.rateProgramId)}
                            </div>
                        );

                        return (
                        <Card key={prog.rateProgramId} className="mb-3">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <Badge
                                            className="d-flex align-items-center"
                                            style={{ fontSize: ".75rem" }}
                                        >
                                            {badgeLabel}
                                        </Badge>
                                        <span className="fs-5 fw-bold">{prog.name}</span>
                                        {isCode && prog.code && (
                                            <span
                                                className="d-inline-flex align-items-center gap-1 rounded-2 px-2 py-1"
                                                style={{
                                                    border: "2px dotted var(--bs-primary)",
                                                    color: "var(--bs-primary)",
                                                    fontSize: ".75rem",
                                                    fontWeight: 600,
                                                    letterSpacing: "0.06em",
                                                }}
                                            >
                                                <TicketPerforatedFill size={13} />
                                                {prog.code.toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    {rateActiveControls(prog)}
                                </div>

                                {isCode && (
                                    <p className="text-muted mb-0" style={{ fontSize: ".875rem" }}>
                                        Customer enters this code at checkout to unlock the discount
                                    </p>
                                )}

                                <RateProgramTable prog={prog} hideCode headerAction={validationActions} />
                            </Card.Body>
                        </Card>
                        );
                    })}
                    </>}
                </Tab>
                {(!programs || programs.length === 0) && 
                    <div className="alert alert-light-bg mb-3 d-flex align-items-center gap-2">
                       
                        No rate programs exist yet. Create your first rate program by clicking Create.
                    </div>
                }
            </Tabs>
            </Col>
        </Row>
        <FacilityRateTesterModal
            show={showRateTesterModal}
            onHide={() => setShowRateTesterModal(false)}
            facilityId={facilityId}
        />
        <ValidationsInfoModal
            show={showValidationsInfoModal}
            onHide={() => setShowValidationsInfoModal(false)}
        />
    </Container>;
}

interface RateProgramTableProps {
    prog: RateProgram;
    hideCode?: boolean;
    headerAction?: ReactNode;
}

export function RateProgramTable( { prog, hideCode, headerAction }: RateProgramTableProps ) {
    const applicableDaysText = showApplicableDays(prog.applicableDays);
    const showDaysRow = applicableDaysText !== "Any day";
    const isAnyTime = !prog.inAfter && !prog.inBefore && !prog.outAfter && !prog.outBefore;
    const isCode = prog.validationType === ValidationType.Code;
    const isDiscountOrCode = prog.validationType === ValidationType.Discount || isCode;
    const priceLabel = isDiscountOrCode ? "Discount" : "Price";

    function formatEntryAmount(rate: RateProgram["entries"][number]) {
        if (rate.discountType === RateEntryDiscountType.Percent) {
            const pct = toCents(rate.rate) / 100;
            return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`;
        }
        if (rate.discountType === RateEntryDiscountType.SetPrice) {
            return `Set to ${showMoneyShort(rate.rate)}`;
        }
        return showMoneyShort(rate.rate);
    }

    return <div className="mb-0">
        {isCode && prog.code && !hideCode && (
            <div className="text-muted mb-2 px-1" style={{ fontSize: ".8rem" }}>
                <strong>Code:</strong> {prog.code}
            </div>
        )}
        {isAnyTime ? (
            prog.isValidation ? (
                headerAction && (
                    <div className="d-flex justify-content-end align-items-center mb-2 px-1">
                        {headerAction}
                    </div>
                )
            ) : (
                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <span style={{ fontSize: ".9rem" }}>Any time</span>
                    {headerAction ?? (
                        <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: ".72rem", letterSpacing: "0.08em" }}>
                            {priceLabel}
                        </span>
                    )}
                </div>
            )
        ) : (
            <>
                <div className="rounded-3 px-3 py-2 mb-2 d-flex flex-wrap gap-3"
                     style={{ backgroundColor: "rgba(255, 100, 130, 0.08)", border: "1px solid rgba(255, 100, 130, 0.2)", fontSize: ".85rem" }}>
                    {prog.inAfter && prog.inBefore
                        ? <span><strong>Check-In</strong> {showTime(prog.inAfter)} – {showTime(prog.inBefore)}</span>
                        : prog.inBefore
                            ? <span><strong>Check-In</strong> Before {showTime(prog.inBefore)}</span>
                            : prog.inAfter
                                ? <span><strong>Check-In</strong> After {showTime(prog.inAfter)}</span>
                                : null}
                    {prog.outAfter && prog.outBefore
                        ? <span><strong>Check-Out</strong> {showTime(prog.outAfter)} – {showTime(prog.outBefore)}</span>
                        : prog.outBefore
                            ? <span><strong>Check-Out</strong> Before {showTime(prog.outBefore)}</span>
                            : prog.outAfter
                                ? <span><strong>Check-Out</strong> After {showTime(prog.outAfter)}</span>
                                : null}
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1 px-1">
                    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: ".72rem", letterSpacing: "0.08em" }}>
                        {priceLabel}
                    </span>
                    {headerAction}
                </div>
            </>
        )}

        {showDaysRow && (
            <div className="text-muted mb-2 px-1" style={{ fontSize: ".8rem" }}>
                <strong>Days:</strong> {applicableDaysText}
            </div>
        )}

        <div className="d-flex flex-column gap-1">
            {prog?.entries.map( ( rate, i ) => {
                const entryLabel = prog.isEvent || prog.isSpecialRate
                    ? "Flat Rate"
                    : prog.isValidation && isDiscountOrCode
                        ? rate.discountType === RateEntryDiscountType.SetPrice
                            ? "Rate set to"
                            : "Rate Discounted By"
                        : prog.isValidation
                            ? "Flat Rate"
                            : `Up to ${showDuration(rate.upTo)}`;
                return <div key={i} className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                            style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", fontSize: ".9rem" }}>
                    <span>
                        {entryLabel}
                    </span>
                    <span className="fw-bold">
                        {formatEntryAmount(rate)}
                    </span>
                </div>;
            })}
        </div>
    </div>;
}